import React, { useState, useMemo, useEffect } from 'react';
import DefaultButton from 'components/Utils/NewDefaultButton';
import MiscUtils from 'services/MiscUtils';
import './style.scss';
import { MinistryOHIPGenderEnum } from 'generated/api';
import PatientUtils from 'services/PatientUtils';
import CPPUtils from 'services/CPPUtils';
import { useAppSelector } from 'careand-redux/store';

//https://www.mdcalc.com/calc/3992/stop-bang-score-obstructive-sleep-apnea
const StopBangCalculatorModal = ({ patient, onSubmit }) => {
  const hr = useAppSelector((state) => state.healthRecords.byId[patient.id]);
  const measurements = useMemo(() => CPPUtils.getPatientMeasurements(hr), [hr]);
  const [data, setData] = useState(
    QUESTIONS.map((q) => {
      return (
        (q.calculate &&
          q.calculate({ patient, height: measurements.height, weight: measurements.weight })) || [
          0,
          undefined,
        ]
      );
    }),
  );

  const points = useMemo(() => {
    let total = 0;
    QUESTIONS.forEach((question, idx) => {
      total += data[idx][0];
    });
    return total;
  }, [data]);

  const risk = useMemo(() => {
    if (points <= 2) {
      return 'low';
    }
    if (points <= 4) {
      return 'intermediate';
    }
    return 'high';
  }, [points]);

  function getResult() {
    return `Score of ${points} points on the STOP-BANG questionnaire for obstructive sleep apnea (OSA), corresponding to a ${risk} risk for moderate to severe OSA.`;
  }

  async function copyToCliboard() {
    await navigator.clipboard.writeText(getResult());
    MiscUtils.showToastrInfo({
      title: 'STOP-BANG Calculator',
      message: 'Score copied successfully!',
    });
  }

  return (
    <div className="stop-bang-calculator modal">
      <div className="title">STOP-BANG Score for Obstructive Sleep Apnea</div>
      <div className="content">
        <div className="questions">
          {QUESTIONS.map(({ title, subtitle, answers }, questionIndex) => {
            let autocalculated = data[questionIndex][1];
            autocalculated = autocalculated ? ` ${autocalculated}` : undefined;

            return (
              <div className="question">
                {questionIndex > 0 ? <hr /> : null}
                <div className="question-title">
                  <span className="small-title">{title}</span>
                  <span className="autocalculated-label">&nbsp;{autocalculated}</span>
                </div>
                {subtitle ? <span className="subtitle">{subtitle}</span> : null}
                <div className="answers">
                  {answers.map((text, ansIndex) => {
                    let className = 'answer';
                    if (data[questionIndex][0] === ansIndex) className += ' selected';
                    return (
                      <div
                        className={className}
                        onClick={() => {
                          setData((prevState) => {
                            return {
                              ...prevState,
                              [questionIndex]: [ansIndex, undefined],
                            };
                          });
                        }}
                      >
                        <span>{text}</span>
                        <span className="points">{ansIndex > 0 ? `+${ansIndex}` : ansIndex}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="results">
          <div className="row points">
            <span className="points-text">
              <b>{points}</b> points
            </span>
            <div className="button-wrapper">
              <DefaultButton
                icon={onSubmit ? undefined : 'copy'}
                name={onSubmit ? 'Submit' : 'Copy'}
                onClick={
                  onSubmit
                    ? () => {
                        onSubmit(getResult());
                      }
                    : copyToCliboard
                }
                style={{ width: '100px' }}
              />
            </div>
          </div>
          <div className="row">
            <strong className="capitalize">{risk}</strong>&nbsp; risk for moderate to severe OSA
          </div>
        </div>
      </div>
    </div>
  );
};

const QUESTIONS = [
  {
    title: 'Snore',
    subtitle: 'Ask: Do you snore loudly?',
    answers: ['No', 'Yes'],
  },
  {
    title: 'Tiredness',
    subtitle: 'Ask: Do you often feel tired, fatigued, or sleepy during the daytime?',
    answers: ['No', 'Yes'],
  },
  {
    title: 'Apnea',
    subtitle: 'Ask: Has anyone observed you stop breathing during sleep?',
    answers: ['No', 'Yes'],
  },
  {
    title: 'BMI',
    // subtitle: 'Ask "Do you feel nervous?"',
    answers: ['<= 35 Kg/m2', '> 35 Kg/m2'],
    calculate: ({ height, weight }) => {
      if (height === undefined || weight === undefined) {
        return;
      }
      const bmi = CPPUtils.calculateBMI(height, weight);
      const text = getAutoCalculatedExplanation(
        `bmi: ${bmi} from height: ${height}cm and weight: ${weight}Kg`,
      );
      if (bmi <= 35) {
        return [0, text];
      }
      return [1, text];
    },
  },
  {
    title: 'Age',
    answers: ['<= 50 years', '>= 50 years'],
    calculate: ({ patient }) => {
      let age = PatientUtils.getAgeAsNumber(patient);
      if (!age) {
        return;
      }
      const text = getAutoCalculatedExplanation(`age: ${age}`);
      if (age <= 50) {
        return [0, text];
      }
      return [1, text];
    },
  },
  {
    title: 'Neck Circumference',
    // subtitle: 'Ask, "Have you any itching, pins and needles sensations, any burning, any numbness, or do you feel bugs crawling on or under your skin?"',
    answers: ['<= 40 cm', '> 40 cm'],
  },
  {
    title: 'Gender',
    // subtitle: 'Ask, "Are you more aware of sounds around you? Are they harsh? Do they frighten you? Are you hearing anything that is disturbing to you? Are you hearing things you know are not there?"',
    answers: ['Female', 'Male'],
    calculate: ({ patient }) => {
      switch (patient.gender) {
        case MinistryOHIPGenderEnum.Female:
          return [0, getAutoCalculatedExplanation()];
        case MinistryOHIPGenderEnum.Male:
          return [1, getAutoCalculatedExplanation()];
        default:
          return undefined;
      }
    },
  },
];

function getAutoCalculatedExplanation(str) {
  let extra = '';
  if (str !== undefined) {
    extra = ` (${str})`;
  }
  return `Calculated from patient data${extra}`;
}

export default StopBangCalculatorModal;
